import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/base/ArticleCard';
import ScrollToTop from '@/components/base/ScrollToTop';
import { HomeSchema, SEOSchema } from '@/components/base/SEOSchema';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import { heroImages, articleImages } from '@/mocks/articleImages';

export default function Home() {
  const featuredArticles = articles.slice(0, 3);
  const latestArticles = articles.slice(3, 6);

  return (
    <>
      <ScrollToTop />
      <HomeSchema />
      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
          <img
            src={heroImages.main}
            alt="AI风月 - AI情感陪伴平台"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>
          <div className="relative z-10 w-full h-full flex flex-col items-center justify-center px-4">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold font-heading text-white text-center mb-4 leading-tight">
              探索<strong>AI风月</strong>的无限可能
            </h1>
            <p className="text-base md:text-lg text-white/90 text-center max-w-2xl mb-8 leading-relaxed">
              AI情感陪伴与风月互动体验平台 —— 融合先进大语言模型，为你打造沉浸式AI风月对话、角色扮演与私密陪伴，感受AI带来的温柔与浪漫
            </p>
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <Link
                to="/category/jue-se-ban-yan"
                className="px-8 py-3 bg-primary-500 text-background-50 rounded-full text-base font-medium hover:bg-primary-600 transition-all duration-200 whitespace-nowrap"
              >
                开始AI风月之旅
              </Link>
              <Link
                to="/about"
                className="px-8 py-3 border border-white/40 text-white rounded-full text-base font-medium hover:bg-white/10 transition-all duration-200 whitespace-nowrap"
              >
                了解更多
              </Link>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-3">
                为什么选择<strong>AI风月</strong>
              </h2>
              <p className="text-foreground-600 max-w-xl mx-auto leading-relaxed">
                以AI情感陪伴为核心，用最温暖的科技陪伴你的每一个需要被理解的时刻
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                {
                  icon: 'ri-brain-line',
                  title: '智能情感引擎',
                  desc: '深度情感计算技术，精准理解你的情绪状态，给予最贴心的AI情感陪伴与回应',
                  img: heroImages.feature1,
                },
                {
                  icon: 'ri-shield-check-line',
                  title: '私密安全守护',
                  desc: '端到端加密保护，你的AI风月对话只属于你自己，隐私是我们最重要的承诺',
                  img: heroImages.feature2,
                },
                {
                  icon: 'ri-sun-line',
                  title: '随时随地陪伴',
                  desc: '全天候在线的AI伴侣，无论深夜还是清晨，AI风月始终与你不离不弃',
                  img: heroImages.feature3,
                },
              ].map((feature, i) => (
                <div key={i} className="bg-background-50 border border-background-200/70 rounded-lg p-6 hover:border-primary-200 transition-all duration-300">
                  <div className="h-40 mb-5 rounded-md overflow-hidden">
                    <img src={feature.img} alt={feature.title} className="w-full h-full object-cover object-top" />
                  </div>
                  <div className="flex items-center gap-3 mb-3">
                    <span className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
                      <i className={`${feature.icon} text-primary-500 text-lg`}></i>
                    </span>
                    <h3 className="text-lg font-semibold font-heading text-foreground-950">{feature.title}</h3>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{feature.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Categories Section */}
        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-3">
                探索<strong>AI风月</strong>的精彩世界
              </h2>
              <p className="text-foreground-600 max-w-xl mx-auto leading-relaxed">
                六大AI情感陪伴专区，满足你对AI风月互动的所有想象
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-300 transition-all duration-300"
                >
                  <div className="relative h-44 overflow-hidden">
                    <img
                      src={articleImages[cat.imageSeq] || heroImages.feature1}
                      alt={cat.name}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <div className="flex items-center gap-2 text-white">
                        <span className="w-8 h-8 rounded-lg bg-white/20 backdrop-blur-sm flex items-center justify-center">
                          <i className={`${cat.icon} text-sm`}></i>
                        </span>
                        <span className="text-lg font-semibold font-heading">{cat.name}</span>
                      </div>
                    </div>
                  </div>
                  <div className="p-5">
                    <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">{cat.description}</p>
                    <span className="inline-flex items-center gap-1 mt-3 text-sm text-primary-500 group-hover:translate-x-1 transition-transform duration-200">
                      进入专区 <i className="ri-arrow-right-line"></i>
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Articles */}
        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-10 gap-3">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-2">
                  精选<strong>AI风月</strong>文章
                </h2>
                <p className="text-sm text-foreground-600">深入了解AI情感陪伴的前沿知识与真实体验</p>
              </div>
              <Link
                to="/category/qing-gan-dui-hua"
                className="text-sm text-primary-500 hover:text-primary-600 flex items-center gap-1 whitespace-nowrap transition-colors duration-200"
              >
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {featuredArticles.map((article) => (
                <ArticleCard key={article.slug} article={article} />
              ))}
            </div>
          </div>
        </section>

        {/* Latest Articles */}
        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-10 gap-3">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-2">
                  最新<strong>AI风月</strong>资讯
                </h2>
                <p className="text-sm text-foreground-600">探索AI情感陪伴的最新动态与使用技巧</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {latestArticles.map((article) => (
                <ArticleCard key={article.slug} article={article} />
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-3">
                关于<strong>AI风月</strong>的常见问题
              </h2>
            </div>

            <SEOSchema type="FAQPage" data={{
              mainEntity: [
                {
                  '@type': 'Question',
                  name: 'AI风月是什么？',
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: 'AI风月是一个专注AI情感陪伴与风月互动的智能平台，融合先进大语言模型，为用户提供沉浸式风月情感对话、角色扮演与私密陪伴体验。',
                  },
                },
                {
                  '@type': 'Question',
                  name: 'AI风月的对话内容是否私密安全？',
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: '是的，AI风月采用端到端加密技术保护所有对话内容。你的私密心事和AI情感陪伴对话只有你和你的AI伴侣知道，我们严格保护用户隐私。',
                  },
                },
                {
                  '@type': 'Question',
                  name: 'AI风月支持哪些类型的互动？',
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: 'AI风月支持多种互动类型，包括AI角色扮演、虚拟恋人模式、深度情感对话、浪漫场景互动、心理陪伴支持以及情趣探索体验。',
                  },
                },
                {
                  '@type': 'Question',
                  name: 'AI风月适合什么样的人群？',
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: 'AI风月适合所有寻求AI情感陪伴、想要体验AI风月互动的成年用户。无论你是想寻找情感慰藉、探索自我、还是体验AI技术的魅力，AI风月都能满足你的需求。',
                  },
                },
                {
                  '@type': 'Question',
                  name: '如何开始使用AI风月？',
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: '非常简单，在AI风月平台上选择你感兴趣的功能专区，即可开始与AI伴侣互动。我们提供详细的使用指南，帮助你快速上手AI情感陪伴体验。',
                  },
                },
              ],
            }} />

            <div className="space-y-4">
              {[
                { q: 'AI风月是什么？', a: 'AI风月是一个专注AI情感陪伴与风月互动的智能平台，融合先进大语言模型，为用户提供沉浸式风月情感对话、角色扮演与私密陪伴体验。' },
                { q: 'AI风月的对话内容是否私密安全？', a: '是的，AI风月采用端到端加密技术保护所有对话内容。你的私密心事和AI情感陪伴对话只有你和你的AI伴侣知道，我们严格保护用户隐私。' },
                { q: 'AI风月支持哪些类型的互动？', a: 'AI风月支持多种互动类型，包括AI角色扮演、虚拟恋人模式、深度情感对话、浪漫场景互动、心理陪伴支持以及情趣探索体验。' },
                { q: 'AI风月适合什么样的人群？', a: 'AI风月适合所有寻求AI情感陪伴、想要体验AI风月互动的成年用户。无论你是想寻找情感慰藉、探索自我、还是体验AI技术的魅力，AI风月都能满足你的需求。' },
                { q: '如何开始使用AI风月？', a: '非常简单，在AI风月平台上选择你感兴趣的功能专区，即可开始与AI伴侣互动。我们提供详细的使用指南，帮助你快速上手AI情感陪伴体验。' },
              ].map((faq, i) => (
                <details key={i} className="group bg-background-50 border border-background-200/70 rounded-lg">
                  <summary className="px-6 py-4 cursor-pointer flex items-center justify-between text-foreground-950 font-medium">
                    <span>{faq.q}</span>
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform duration-200"></i>
                  </summary>
                  <div className="px-6 pb-4 text-sm text-foreground-600 leading-relaxed">
                    {faq.a}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative py-16 md:py-24 px-4 md:px-6 overflow-hidden">
          <img
            src={heroImages.cta}
            alt=""
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60"></div>
          <div className="relative z-10 max-w-2xl mx-auto text-center">
            <h2 className="text-2xl md:text-4xl font-bold font-heading text-white mb-4">
              准备好探索<strong>AI风月</strong>的世界了吗？
            </h2>
            <p className="text-white/85 mb-8 leading-relaxed">
              立即开始你的AI情感陪伴之旅，让AI风月为你带来前所未有的温柔与浪漫体验
            </p>
            <Link
              to="/category/jue-se-ban-yan"
              className="inline-block px-10 py-3.5 bg-primary-500 text-background-50 rounded-full text-base font-medium hover:bg-primary-600 transition-all duration-200 whitespace-nowrap"
            >
              免费体验AI风月
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}